// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — reject mapping (ABB-S4, SM-2 / step 6)
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps a THROW from the safe contour (or the pre-commit self-check, or the
// precondition gate) to the canonical rpc-error `{code, detail}` the companion's
// `mapBrowserCodeToHttp` (`browserCodeMap.ts`) classifies. Every code emitted
// here is a member of `BROWSER_EMITTED_REJECT_CODES` (the S2 taxonomy single
// source of truth) — a code not in that set would fall to the companion's 500
// `browser-dispatch-error` default, mis-signalling a deterministic dispatch
// failure for a benign reject (P2).
//
// MAPPING (explicit `instanceof` per class — P2: the four contour classes must
// NEVER reach the 500 fallback; `commandRun.test.ts` asserts this):
//   • AiCommandValidationError   → ai-command-rejected 422 {reason, issues}
//   • NonAiCallableCommandError  → ai-command-rejected 422 {reason}
//   • ViewSpaceContextError      → ai-command-rejected 422 {reason}
//   • AiColorValidationError     → ai-command-rejected 422 {value}
//   • NonSerializableBatchError  → ai-command-rejected 422 {reason:non-serializable-batch}
//   • PreconditionStaleError     → manifest/snapshot/geometry/table/context-stale 409
//   • SupersededError            → superseded-by-takeover  (companion → 503)
//   • DeadlineError              → request-timeout-aborted (companion → 504)
//   • CommandInFlightError       → command-in-flight       409
//   • ANY OTHER (non-contour)    → browser-dispatch-error  500 (the genuine
//                                  dispatch-exception fallback — a contour class
//                                  here is a BUG)
//
// LEAF module: imports ONLY the four contour error classes from `ai-commands`
// (an ALLOWED dependency direction) + the local typed bridge errors. No socket,
// no editorStore, no node-companion (ПРИОРИТЕТ #2).
// ═══════════════════════════════════════════════════════════════════════════

import {
  AiColorValidationError,
  AiCommandValidationError,
  NonAiCallableCommandError,
  ViewSpaceContextError,
} from "@dxf-ai-editor/ai-commands";
import type { BrowserEmittedRejectCode } from "@dxf-ai-editor/ai-commands/manifest";

import { NonSerializableBatchError } from "./batchHash";

/** The set of precondition-staleness codes (ABB-S4 step 2 / SM-4). */
export type PreconditionStaleCode =
  | "manifest-stale"
  | "snapshot-stale"
  | "geometry-stale"
  | "table-stale"
  | "context-stale";

/**
 * A precondition mismatch (the browser-side optimistic-concurrency gate, INV-1).
 * Carries the SPECIFIC stale code + the (base, live) pair for the detail so the
 * agent re-reads the right state. `force` does NOT bypass these (force is
 * revision-ONLY) — but the SESSION/document quad gate is the companion's, not
 * this; this error is only ever the revision/manifest mismatch.
 */
export class PreconditionStaleError extends Error {
  readonly code: PreconditionStaleCode;
  readonly base: unknown;
  readonly live: unknown;

  constructor(code: PreconditionStaleCode, base: unknown, live: unknown) {
    super(
      `agent-bridge precondition mismatch (${code}): base=${String(base)} live=${String(live)}`,
    );
    this.name = "PreconditionStaleError";
    this.code = code;
    this.base = base;
    this.live = live;
  }
}

/**
 * Thrown by the pre-commit self-check when this tab was DEMOTED before the turn
 * could commit (epoch/role demotion intercepted at `history/commit-batch`). The
 * contour's own catch fires `history/abort-batch` and rethrows → a TRUE pre-commit
 * rollback. Maps to `superseded-by-takeover` (the companion aliases it onto 503).
 */
export class SupersededError extends Error {
  constructor(message = "agent-bridge turn superseded before commit (demoted)") {
    super(message);
    this.name = "SupersededError";
  }
}

/**
 * Thrown by the pre-commit self-check when the per-request deadline (armed against
 * the browser's OWN `performance.now` origin) elapsed before commit. The contour's
 * own catch fires `history/abort-batch` and rethrows → a TRUE pre-commit rollback.
 * Maps to `request-timeout-aborted` (the companion → 504), DISTINCT from the
 * companion-side `request-timeout` (the agent's budget).
 */
export class DeadlineError extends Error {
  constructor(message = "agent-bridge turn exceeded its deadline before commit") {
    super(message);
    this.name = "DeadlineError";
  }
}

/**
 * Thrown when the shared single-in-flight latch is already held by ANOTHER in-page
 * client (panel / window-seam) — the bridge refuses rather than nesting into the
 * open batch. Maps to `command-in-flight` 409 (the closest taxonomy code; it is a
 * RETRYABLE 409 in the taxonomy). The companion's `command-in-flight` covers
 * bridge-vs-bridge; THIS instance covers bridge-vs-LOCAL.
 */
export class CommandInFlightError extends Error {
  constructor(message = "another in-page client holds the single-in-flight latch") {
    super(message);
    this.name = "CommandInFlightError";
  }
}

/**
 * Thrown by the handler when the forwarded payload is structurally malformed (no
 * `batch` array) — an agent grammar error that never reaches the contour's
 * `parseAiCommandBatch`. Maps to `ai-command-rejected` 422 (NOT a 500 dispatch
 * failure — the dispatch never started; the request shape is wrong).
 */
export class MalformedBatchError extends Error {
  constructor(message = "command.run payload is missing a `batch` array") {
    super(message);
    this.name = "MalformedBatchError";
  }
}

/**
 * Thrown when an optimistic-concurrency precondition field is PRESENT on the wire
 * but WRONGLY TYPED — a `base{Snapshot,Geometry,Table,Context}Revision` /
 * `manifestVersion` present but not the expected type (e.g. a STRING `"5"` instead
 * of the number `5`, or a `NaN`/non-finite number). The companion forwards these
 * fields OPAQUELY (INV-1, `correlation.ts`), so a wrongly-typed value reaches the
 * handler. Treating it as ABSENT would SILENTLY DROP the optimistic guard the
 * agent believed it supplied → the command runs against the live revision (the
 * data-integrity hole; `assertJsonRepresentable` rejects a non-finite NUMBER but a
 * STRING `"5"` slips past it). So a present-but-malformed precondition value is
 * REJECTED before any dispatch. Maps to `ai-command-rejected` 422 (a bad ENVELOPE
 * value — the browser-emitted reject taxonomy has no browser-emittable
 * `malformed-request`: that code is COMPANION-only at the route and is NOT in
 * `KNOWN_BROWSER_CODES`, so emitting it from the browser would 500-fall-through,
 * `browserCodeMap.ts`). Carries the offending field name so the agent fixes the
 * right key.
 */
export class MalformedPreconditionError extends Error {
  readonly field: string;

  constructor(field: string) {
    super(
      `command.run precondition \`${field}\` is present but not a finite number — malformed envelope value`,
    );
    this.name = "MalformedPreconditionError";
    this.field = field;
  }
}

/** The canonical rpc-error body a reject maps to (the handler returns `{ok:false, ...}`). */
export interface MappedReject {
  readonly code: BrowserEmittedRejectCode;
  readonly message: string;
  readonly detail?: unknown;
}

/**
 * Map a thrown error to its canonical rpc-error `{code, message, detail}`. Explicit
 * `instanceof` per class so a contour class can NEVER silently reach the 500
 * fallback (P2). The order is: the typed bridge errors (precondition / pre-commit /
 * latch) first, then the four contour classes, then the non-serializable-batch
 * sibling, then the genuine 500 fallback for any OTHER (non-contour) throw.
 */
export function mapRejectToRpcError(error: unknown): MappedReject {
  // ── typed bridge errors (precondition gate / pre-commit self-check / latch) ──
  if (error instanceof PreconditionStaleError) {
    return {
      code: error.code,
      message: error.message,
      detail: { base: error.base, live: error.live },
    };
  }
  if (error instanceof SupersededError) {
    return { code: "superseded-by-takeover", message: error.message };
  }
  if (error instanceof DeadlineError) {
    return { code: "request-timeout-aborted", message: error.message };
  }
  if (error instanceof CommandInFlightError) {
    return { code: "command-in-flight", message: error.message };
  }
  if (error instanceof MalformedBatchError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: { reason: "malformed-batch" },
    };
  }
  if (error instanceof MalformedPreconditionError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: { reason: "malformed-precondition", field: error.field },
    };
  }

  // ── the FOUR safe-contour throw classes → ai-command-rejected 422 ──────────
  if (error instanceof AiCommandValidationError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: {
        reason: error.action ?? "schema-validation",
        // The full Zod issue list so the agent can fix the offending paths.
        issues: error.zodError.issues,
      },
    };
  }
  if (error instanceof NonAiCallableCommandError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: { reason: error.reason, commandId: error.commandId },
    };
  }
  if (error instanceof ViewSpaceContextError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: {
        reason: error.reason,
        ...(error.spaceId !== undefined ? { spaceId: error.spaceId } : {}),
      },
    };
  }
  if (error instanceof AiColorValidationError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: { reason: "invalid-color", value: error.value },
    };
  }

  // ── non-serializable batch (the hash/serialization fail-closed reject) ─────
  if (error instanceof NonSerializableBatchError) {
    return {
      code: "ai-command-rejected",
      message: error.message,
      detail: { reason: "non-serializable-batch" },
    };
  }

  // ── genuinely-unexpected NON-contour throw → 500 (a contour class here = BUG) ─
  return {
    code: "browser-dispatch-error",
    message: error instanceof Error ? error.message : "browser dispatch threw",
  };
}
